import {
  Briefcase,
  CircleDashed,
  ClockCounterClockwise,
  IdentificationCard,
  LinkedinLogo,
  MapPin,
  UserList,
} from "@phosphor-icons/react";
import { useMemo } from "react";

import {
  ColumnsVisibilityFilter,
  PaginatedDataTable,
} from "common/components/DataTable";
import usePaginatedDataTable from "common/components/DataTable/usePaginatedDataTable";
import ProfileImage from "common/components/ui/ProfileImage";
import Skeleton from "common/components/ui/Skeleton";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";

import LeadCompanyTimeTag from "../../../../../../leads/components/AllLeads/LeadTags/LeadCompanyTimeTag";
import LeadDetailTag from "../../../../../../leads/components/AllLeads/LeadTags/LeadDetailTag";
import AssignedToTag from "../AssignedToTag";
import AudienceStateTag from "../AudienceStateTag";
import AudienceTargetActions from "./AudienceTargetActions";
import ManageDesktopAudience from "./ManageDesktopAudience";

import type { ColumnDef } from "@tanstack/react-table";
import type { Dispatch, ReactNode, SetStateAction } from "react";
import type { AudienceRow } from "../../../../../types";

interface AudienceDesktopTableProps {
  rows: AudienceRow[];
  isLoading: boolean;
  selectedTargets: AudienceRow[];
  setSelectedTargets: Dispatch<SetStateAction<AudienceRow[]>>;
  openLeadPreview: (leadId: string) => void;
  filtersComponent: ReactNode;
  emptyStateComponent: ReactNode;
}

export default function AudienceDesktopTable({
  rows,
  isLoading,
  selectedTargets,
  setSelectedTargets,
  openLeadPreview,
  filtersComponent,
  emptyStateComponent,
}: AudienceDesktopTableProps) {
  const { permissions } = useSelectedWorkspaceContext();
  const canOpenLeadPreview = permissions.includes("leads_full");

  const columns = useMemo<ColumnDef<AudienceRow>[]>(
    () => [
      {
        id: "target",
        header: "Target",
        cell: ({ row: { original } }) => {
          const {
            picture_url: pictureUrl,
            full_name: fullName,
            premium,
          } = original;

          return (
            <div className="flex items-center gap-x-2.5">
              {isLoading ? (
                <Skeleton className="size-10 rounded-full" />
              ) : (
                <ProfileImage src={pictureUrl} />
              )}
              <div className="flex flex-col gap-y-0.5">
                {isLoading ? (
                  <>
                    <Skeleton className="h-5 w-28" />
                    <Skeleton className="h-4 w-44" />
                  </>
                ) : (
                  <div className="flex items-center gap-x-1">
                    <span className="line-clamp-1 break-all text-xs-bold">
                      {fullName}
                    </span>
                    {premium && (
                      <LinkedinLogo
                        className="size-4 text-yellow-500"
                        weight="fill"
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        },
        enableHiding: false,
      },
      {
        id: "occupation",
        header: "Occupation",
        cell: ({ row: { original } }) => {
          return isLoading ? (
            <Skeleton className="h-4 w-24" />
          ) : (
            <span className="line-clamp-1 max-w-[40ch] break-all">
              {original.occupation}
            </span>
          );
        },
        meta: { icon: IdentificationCard, className: "min-w-40 max-w-40" },
      },
      {
        id: "assignedTo",
        header: "Assigned to",
        cell: ({ row: { original } }) => {
          if (isLoading) {
            return <Skeleton className="h-6 w-24 rounded-full" />;
          }

          if (original.owner) {
            return <AssignedToTag owner={original.owner} size="md" />;
          }
          return "Unassigned";
        },
        meta: { icon: UserList, className: "min-w-40 max-w-40" },
      },
      {
        id: "state",
        header: "State",
        cell: ({
          row: {
            original: { state, cancellationDetails },
          },
        }) => {
          if (isLoading) {
            return <Skeleton className="h-6 w-20" />;
          }
          return state ? (
            <AudienceStateTag
              size="md"
              state={state}
              cancellationDetails={cancellationDetails}
            />
          ) : (
            "-"
          );
        },
        meta: { icon: CircleDashed, className: "min-w-40 max-w-40" },
      },
      {
        id: "currentCompanies",
        header: "Current company",
        cell: ({
          row: {
            original: { id },
          },
        }) => (
          <LeadDetailTag
            leadKey="current_experience"
            leadId={id}
            isLoading={isLoading}
          />
        ),
        meta: { icon: Briefcase, className: "min-w-40 max-w-40" },
      },
      {
        id: "currentCompaniesTime",
        header: "Current company time",
        cell: ({
          row: {
            original: { id },
          },
        }) => (
          <LeadCompanyTimeTag
            leadKey="current_experience"
            leadId={id}
            isLoading={isLoading}
          />
        ),
        meta: { icon: Briefcase, className: "min-w-40 max-w-40" },
      },
      {
        id: "pastCompanies",
        header: "Past company",
        cell: ({
          row: {
            original: { id },
          },
        }) => (
          <LeadDetailTag
            leadKey="work_experience"
            leadId={id}
            isLoading={isLoading}
          />
        ),
        meta: { icon: ClockCounterClockwise, className: "min-w-40 max-w-40" },
      },
      {
        id: "pastCompaniesTime",
        header: "Past company time",
        cell: ({
          row: {
            original: { id },
          },
        }) => (
          <LeadCompanyTimeTag
            leadKey="work_experience"
            leadId={id}
            isLoading={isLoading}
          />
        ),
        meta: { icon: ClockCounterClockwise, className: "min-w-40 max-w-40" },
      },
      {
        id: "location",
        header: "Location",
        cell: ({ row: { original } }) => {
          return isLoading ? (
            <Skeleton className="h-4 w-24" />
          ) : (
            <span className="line-clamp-1 break-all">
              {original.location?.name}
            </span>
          );
        },
        meta: { icon: MapPin, className: "min-w-40 max-w-40" },
      },
      {
        id: "action",
        cell: ({ row: { original } }) => {
          if (isLoading) {
            return <Skeleton className="inline-flex size-8 rounded-full" />;
          }

          return <AudienceTargetActions profile={original} />;
        },
        enableHiding: false,
        meta: { className: "w-0", isSticky: true },
      },
    ],
    [isLoading],
  );

  const { table } = usePaginatedDataTable({
    dataTableOptions: {
      localStorageId: "campaign-details-audience",
      selectedRows: selectedTargets,
      onRowSelectionChange: permissions.includes("campaigns_full")
        ? setSelectedTargets
        : undefined,
    },
    reactTableOptions: { data: rows, columns },
    isLoading,
  });

  function onRowClick(row: AudienceRow) {
    if (!canOpenLeadPreview) {
      return;
    }

    openLeadPreview(row.id);
  }

  return (
    <>
      <div className="flex flex-row items-center gap-x-1.5">
        {filtersComponent}
        <ColumnsVisibilityFilter table={table} />
      </div>

      {!isLoading && !rows.length ? (
        emptyStateComponent
      ) : (
        <PaginatedDataTable
          title="Targets"
          table={table}
          onRowClick={onRowClick}
          isLoading={isLoading}
          selectionActionsComponent={
            <ManageDesktopAudience
              selectedTargets={selectedTargets}
              resetSelectedTargets={() => setSelectedTargets([])}
            />
          }
        />
      )}
    </>
  );
}
